import { useEffect, useState } from 'react';

import { type AxiosRequestConfig } from 'axios';

import { Col, Container, Footer, Row, Header, Typography } from 'darrius';

import { HeadContent, Bundles, Toast } from '../../components';
import { showToast } from '../../components/atoms/Toast/Toast';

import { customTracking } from '../../utils/customTracking';
import { axiosInstance } from '../../utils/axiosInstance';
import { navigate } from '../../utils/navigate';

import { type IWebCheckoutTemplateProps } from './@interfaces';
import {
  CheckoutContext,
  type ICheckoutData,
  initialValue,
} from '../../context/Checkout';

import styles from './WebCheckoutTemplate.module.scss';

declare global {
  interface Window {
    newrelic?: any;
    NREUM?: any;
  }
}

export default function WebCheckoutTemplate({
  staticContent,
  profile,
  authToken,
  bundlesData,
  paymentCards,
}: IWebCheckoutTemplateProps) {
  const { footerContent } = staticContent;
  const [checkoutData, setCheckoutData] = useState<ICheckoutData>(initialValue);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = '/seja-um-profissional/js/newrelic.js';
    script.async = true;
    script.type = 'text/javascript';
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
      delete window.NREUM;
      delete window.newrelic;
    };
  }, []);

  useEffect(() => {
    setCheckoutData((prev) => {
      return { ...prev, profile, bundlesData, authToken };
    });

    if (window?.newrelic) {
      window.newrelic.addPageAction('webcheckout_action', {
        step: 'screen_load',
        profile_id: profile.id,
        store_type: 'GN_STORE',
        context_type: 'three_bundle_store_3_0',
      });
    }

    customTracking({
      schema: 'iglu:br.com.getninjas/purchase_screen_load/jsonschema/1-0-1',
      data: {
        profile_id: profile.id,
        store_type: 'GN_STORE',
        context_type: 'three_bundle_store_3_0',
      },
    });
  }, []);

  async function handleLogoutUser() {
    try {
      const requestOptions: AxiosRequestConfig = {
        url: `${process.env.NEXT_PUBLIC_API_PRO}/logout`,
        method: 'POST',
        data: {
          authToken,
        },
      };

      await axiosInstance(requestOptions);
      navigate('/seja-um-profissional/login');
    } catch (error) {
      showToast({ message: error.response.data.clientMessage, type: 'error' });
    }
  }

  return (
    <CheckoutContext.Provider
      value={{
        checkoutData,
        updateCheckoutData: setCheckoutData,
      }}
    >
      <HeadContent
        metaTitle="GetNinjas | Orçamento de Profissionais Confiáveis no GetNinjas.com.br"
        slug="web checkout"
        metaDescription="GetNinjas - Checkout"
        type="prospect:web-checkout"
      />

      <Header.Root>
        <Header.NavMenu>
          <Header.Auth
            isLogged={!!checkoutData}
            onLogout={handleLogoutUser}
            user={{
              avatar: checkoutData.profile.name,
              name: checkoutData.profile.name,
              phone: checkoutData.profile.phone,
            }}
          />
        </Header.NavMenu>
      </Header.Root>

      <Container className={styles['web-checkout__container']}>
        <Row className="space-top center-sm">
          <Col>
            <Typography.Title size="3xl">Escolha seu pacote:</Typography.Title>
          </Col>
        </Row>

        <Row className="space-top center-sm">
          <Col className="col-md-12 col-sm-11">
            <Bundles appButton={true} origin={'webcheckout'} paymentCards={paymentCards} />
          </Col>
        </Row>

        <Row className="space-top space-bottom center-sm">
          <Col>
            <Typography.Text className={styles.text}>
              🔒 Segurança garantida dos seus dados bancários
            </Typography.Text>
            <img
              src={`${process.env.NEXT_PUBLIC_ASSETS_PREFIX}/images/purchase-icons.svg`}
              alt="Imagem de metodos de pagamentos e bandeiras de cartões de crédito"
            />
          </Col>
        </Row>
      </Container>

      <Footer data={footerContent} />
      <Toast />
    </CheckoutContext.Provider>
  );
}
